import React, { useEffect, useReducer, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Button, CircularProgress } from '@material-ui/core';

import AppInput from '../../../../../../common/AppInput';
import Message from '../../../../../../common/Message';
import AuthorizeCheckbox from '../authorizeThird/AuthorizeCheckbox';

import { schema } from './validation/schema';
import { makeInitialState, inputsInState } from './validation/initialState';

import Loader from '../../../tracking/trackingLoading.gif';

import {
  makeSelfServiceByMotive,
  validateProductByMotive,
} from '../../../../../../../redux/actions/TicketActions';

import {
  dispatchGenerator,
  genericSelfServiceBody,
  successCounterInState,
  validationBodyGenerator,
} from '../../../../../../../helpers/tickets/SelfService';
import { validateForm } from '../validation';
import { changeDispatchAddressErrorMsg } from '../../../../../../../constants/Messages';

import { dispatchAddressStyles } from './styles';

const reducer = (state, { field, value }) => {
  if (field === 'reset') return value;
  return { ...state, [field]: value };
};

const defineSeverity = (mobile) => (mobile ? 'info' : 'info-blue-background');

const defineCommentHelper = (name, newValue) =>
  name === 'comment' ? `${newValue.length}/250` : '';

const ButtonTitle = ({ selfServiceStatus }) => {
  return (
    <React.Fragment>
      {selfServiceStatus === 'loading' ? (
        <CircularProgress size={24} />
      ) : (
        'Enviar'
      )}
    </React.Fragment>
  );
};

ButtonTitle.propTypes = {
  selfServiceStatus: PropTypes.string,
};

const ChangeDispatchAddress = ({ ...props }) => {
  const {
    mobile,
    motiveId,
    product,
    type,
    onData,
    profile,
    selfServiceStatus,
  } = props;
  const [state, dispatch] = useReducer(reducer, makeInitialState());
  const [valid, setValid] = useState(false);
  const [checked, setChecked] = useState(false);
  const { validationStatus, validation } = useSelector(
    (innerState) => innerState.ticketReducer,
  );
  const classes = dispatchAddressStyles({ mobile });
  const reduxDispatch = useDispatch();

  const handleValidation = (event) => {
    validateForm(event.target, dispatch, schema, state);
  };

  const handleInputChange = (event) => {
    const { name, value: newValue } = event.target;
    const body = {
      newValue,
      helperText: defineCommentHelper(name, newValue),
    };
    const dispatchBody = dispatchGenerator(state, name, body);
    dispatch(dispatchBody);
  };

  const selfServiceBodyGenerator = () => ({
    ...genericSelfServiceBody({ product, profile, type, validation }),
    newAddress: state['newAddress'].value,
    commune: validation.commune,
    comment: state['comment'].value,
  });

  const clickSelfServiceButton = () => {
    reduxDispatch(
      makeSelfServiceByMotive(motiveId, selfServiceBodyGenerator()),
    );
    onData({
      motiveId,
      message: `${state['newAddress'].value}, ${validation.commune}`,
    });
  };

  useEffect(() => {
    if (validation?.commune) {
      const { commune: newValue } = validation;
      const dispatchBody = dispatchGenerator(state, 'commune', { newValue });
      dispatch(dispatchBody);
    }
  }, [validation]);

  useEffect(() => {
    if (mobile) {
      const body = validationBodyGenerator(props);
      reduxDispatch(validateProductByMotive(motiveId, body));
    }
  }, []);

  useEffect(() => {
    setValid(successCounterInState(state));
  }, [state]);

  return (
    <React.Fragment>
      {validationStatus === 'validating' && (
        <Box display="flex" justifyContent="center" margin="0 auto">
          <img src={Loader} className={classes.loader} />
        </Box>
      )}
      {validationStatus === 'error' && (
        <Box className={classes.container}>
          <Message
            severity="info"
            message={changeDispatchAddressErrorMsg(validation.id)}
            fullRounded
          />
        </Box>
      )}
      {validationStatus === 'succeded' && (
        <Box className={classes.container}>
          <Box>
            <Message
              severity={defineSeverity(mobile)}
              message="El cambio de dirección solo es posible dentro de la misma comuna"
              fullRounded
            />
          </Box>
          {inputsInState.map((input, index) => (
            <AppInput
              key={index}
              name={input}
              required={state[input].required}
              helperText={state[input].helper}
              label={state[input].label}
              value={state[input].value}
              inputAdornment={state[input].inputAdornment}
              onClick={() => {}}
              onBlur={handleValidation}
              onChange={handleInputChange}
              disabled={state[input].disabled}
              multiline={state[input].textArea}
              error={state[input].error}
              errorMessage={state[input].message}
              success={state[input].success}
            />
          ))}
          <AuthorizeCheckbox
            disabled={!valid}
            idComplement={'DispatchAddress'}
            onCheck={(e) => setChecked(e)}
          />
          <Button
            className={classes.button}
            variant="contained"
            color="primary"
            disabled={!valid || !checked || selfServiceStatus === 'loading'}
            onClick={clickSelfServiceButton}
            data-cy="buttonSendChangeDispatchAddress"
          >
            <ButtonTitle selfServiceStatus={selfServiceStatus} />
          </Button>
        </Box>
      )}
    </React.Fragment>
  );
};

ChangeDispatchAddress.propTypes = {
  mobile: PropTypes.bool,
  motiveId: PropTypes.string,
  type: PropTypes.string,
  product: PropTypes.object,
  onData: PropTypes.func,
  profile: PropTypes.object,
  selfServiceStatus: PropTypes.string,
};

export default ChangeDispatchAddress;
